import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Download, FileText, Pencil, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmDelete, Field, SelectField } from "@/components/common";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FILE_CATEGORIES } from "@/lib/constants";

export const fileUrl = (id, download) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${id}/content${download ? "?download=1" : ""}`;
const PDF_OK = ["image/jpeg", "image/png", "image/webp"];
const fmtAdded = (s) => (s ? new Date(s).toLocaleDateString("fr-FR", { timeZone: "Europe/Paris" }) : "—");
const isImg = (f) => f.mime.startsWith("image/") && !f.mime.includes("hei");

const EditDialog = ({ file, categories, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  useEffect(() => { if (file) setForm({ label: file.label || "", category: file.category || "", in_pdf: !!file.in_pdf }); }, [file]);
  const save = async () => {
    try {
      await api.patch(`/files/${file.id}`, categories ? form : { label: form.label });
      toast.success("Pièce jointe mise à jour");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <Dialog open={!!file} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-cyan-400/20 bg-[#0F1F3D]" data-testid="file-edit-dialog">
        <DialogHeader><DialogTitle>Modifier la pièce jointe</DialogTitle></DialogHeader>
        <Field label="Titre ou commentaire"><Input value={form.label || ""} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="file-edit-label" /></Field>
        {categories && <Field label="Classement"><SelectField value={form.category || null} allowNone="Non classé" options={FILE_CATEGORIES} onChange={(v) => setForm({ ...form, category: v || "" })} testid="file-edit-category" /></Field>}
        {categories && file && PDF_OK.includes(file.mime) && (
          <label className="flex items-center gap-3 text-sm text-slate-200">
            <Checkbox checked={!!form.in_pdf} onCheckedChange={(v) => setForm({ ...form, in_pdf: !!v })} data-testid="file-edit-in-pdf" />Intégrer cette photo au bon d'intervention PDF
          </label>
        )}
        <DialogFooter><Button onClick={save} data-testid="file-edit-save">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Tile = ({ f, admin, onEdit, onDelete, categories }) => (
  <div className="overflow-hidden rounded-xl border border-white/10 bg-white/[0.03]" data-testid={`file-${f.id}`}>
    <a href={fileUrl(f.id)} target="_blank" rel="noreferrer" className="relative block aspect-square" data-testid={`file-open-${f.id}`}>
      {isImg(f)
        ? <img src={fileUrl(f.id)} alt={f.label || f.name} className="h-full w-full object-cover" loading="lazy" />
        : <div className="flex h-full flex-col items-center justify-center gap-1 p-2 text-center text-xs text-slate-300"><FileText className="h-7 w-7 text-cyan-300" /><span className="line-clamp-2 break-all">{f.name}</span></div>}
      {f.in_pdf && <span className="absolute left-1.5 top-1.5 rounded bg-cyan-500/90 px-1.5 py-0.5 text-[10px] font-semibold text-[#0A1630]" data-testid={`file-in-pdf-${f.id}`}>Bon PDF</span>}
    </a>
    <div className="space-y-1 p-2">
      <p className="truncate text-xs text-white" title={f.label || f.name}>{f.label || f.name}</p>
      <p className="text-[11px] text-slate-500">{categories && f.category ? `${FILE_CATEGORIES[f.category]} · ` : ""}{fmtAdded(f.created_at)}</p>
      <div className="flex gap-1">
        <button onClick={() => onEdit(f)} className="flex h-9 flex-1 items-center justify-center rounded-md bg-white/5 text-slate-300 hover:text-cyan-300" aria-label="Modifier" data-testid={`file-edit-${f.id}`}><Pencil className="h-4 w-4" /></button>
        <a href={fileUrl(f.id, true)} className="flex h-9 flex-1 items-center justify-center rounded-md bg-white/5 text-slate-300 hover:text-cyan-300" aria-label="Télécharger" data-testid={`file-download-${f.id}`}><Download className="h-4 w-4" /></a>
        {admin && <button onClick={() => onDelete(f)} className="flex h-9 flex-1 items-center justify-center rounded-md bg-white/5 text-red-300 hover:bg-red-500/15" aria-label="Supprimer" data-testid={`file-delete-${f.id}`}><Trash2 className="h-4 w-4" /></button>}
      </div>
    </div>
  </div>
);

const CategoryChips = ({ value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={`${testid}-categories`}>
    {Object.entries(FILE_CATEGORIES).map(([k, label]) => (
      <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${testid}-category-${k}`}
        className={`rounded-full border px-3 py-2 text-sm transition-colors ${value === k ? "border-cyan-400 bg-cyan-400/15 text-cyan-200" : "border-white/10 text-slate-400 hover:text-white"}`}>
        {label}
      </button>
    ))}
  </div>
);

const groupFiles = (files) => {
  const keys = [...Object.keys(FILE_CATEGORIES), ""];
  return keys.map((k) => [k, files.filter((f) => (f.category || "") === k)]).filter(([, l]) => l.length);
};

export const FilesPanel = ({ ownerType, ownerId, title = "Photos et documents", testid = "files-panel" }) => {
  const { user } = useAuth();
  const categories = ownerType === "intervention";
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState("");
  const [cat, setCat] = useState("pendant");
  const [label, setLabel] = useState("");
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const photo = useRef(null);
  const doc = useRef(null);
  const load = useCallback(() => api.get("/files", { params: { owner_type: ownerType, owner_id: ownerId } }).then((r) => setFiles(r.data)), [ownerType, ownerId]);
  useEffect(() => { load(); }, [load]);
  const upload = async (list, input) => {
    try {
      for (const [n, file] of list.entries()) {
        setBusy(`Envoi ${n + 1}/${list.length}…`);
        const fd = new FormData();
        fd.append("owner_type", ownerType);
        fd.append("owner_id", ownerId);
        fd.append("label", label);
        if (categories) fd.append("category", file.type === "application/pdf" ? "document" : cat);
        fd.append("file", file);
        await api.post("/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(list.length > 1 ? `${list.length} fichiers ajoutés` : "Fichier ajouté");
      setLabel("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
      input.value = "";
      load();
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/files/${del.id}`);
      toast.success("Pièce jointe supprimée");
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setDel(null);
    load();
  };
  const tileProps = { admin: user?.role === "admin", onEdit: setEdit, onDelete: setDel, categories };
  return (
    <section className="rounded-xl border border-cyan-400/15 bg-[#122549]/60 p-4" data-testid={testid}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="font-display text-base font-semibold text-white md:text-lg">{title}</h2>
        <span className="text-xs text-slate-400" data-testid={`${testid}-count`}>{files.length ? `${files.length} fichier${files.length > 1 ? "s" : ""}` : ""}</span>
      </div>
      <div className="space-y-3">
        {categories && <CategoryChips value={cat} onChange={setCat} testid={testid} />}
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Titre ou commentaire (facultatif)" data-testid={`${testid}-label-input`} />
        <div className="grid grid-cols-2 gap-2">
          <Button className="h-12" disabled={!!busy} onClick={() => photo.current?.click()} data-testid={`${testid}-photo-button`}><Camera className="mr-2 h-5 w-5" />Photo</Button>
          <Button variant="secondary" className="h-12" disabled={!!busy} onClick={() => doc.current?.click()} data-testid={`${testid}-upload-button`}><Upload className="mr-2 h-5 w-5" />Fichiers</Button>
        </div>
        <p className="text-xs text-slate-500">{busy || "Photos JPEG, PNG, WebP, HEIC ou PDF · 25 Mo maximum par fichier"}</p>
        <input ref={photo} type="file" accept="image/*" capture="environment" hidden onChange={(e) => upload([...e.target.files], e.target)} data-testid={`${testid}-photo-input`} />
        <input ref={doc} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf" multiple hidden onChange={(e) => upload([...e.target.files], e.target)} data-testid={`${testid}-file-input`} />
      </div>
      {!files.length && <p className="mt-4 text-sm text-slate-400" data-testid={`${testid}-empty`}>Aucun fichier.</p>}
      <div className="mt-4 space-y-4">
        {(categories ? groupFiles(files) : [["", files]]).map(([k, list]) => (
          <div key={k || "none"} data-testid={`${testid}-group-${k || "none"}`}>
            {categories && <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-400">{FILE_CATEGORIES[k] || "Non classés"} ({list.length})</h3>}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-5">{list.map((f) => <Tile key={f.id} f={f} {...tileProps} />)}</div>
          </div>
        ))}
      </div>
      <EditDialog file={edit} categories={categories} onClose={() => setEdit(null)} onSaved={load} />
      <ConfirmDelete open={!!del} onOpenChange={(o) => !o && setDel(null)} onConfirm={remove} label={`« ${del?.label || del?.name || ""} »`}
        description="Le fichier sera définitivement retiré de cette fiche et du stockage." />
    </section>
  );
};
